// The sanitised activity TRACE rendering (BUILD-DEC-098 — the owner-approved N-CHAT-3 amendment): what
// the agent DID, rendered EXACTLY from the owning module's durable projection rows — the dashboard
// authors nothing (P-SELF) and the rows are VISUALLY DISTINCT from conversation turns (mono chips /
// cards, never a bubble — DS-CHAT-01: a trace row can never read as a fabricated reply). Labels arrive
// already sanitised (closed vocabulary + client-surface-cleared free text); `objectRef` carries opaque
// click-through refs only. The email card now EXPANDS IN PLACE rather than opening the reader overlay
// (BUILD-DEC-155) and lives with its inbound twin in ChatEmailCard.tsx — one email vocabulary, one file.
// The reasoning summaries no longer take a row each: an adjacent run of them folds into one quiet disclosure
// (owner, 11 Aug 2026), and their markdown emphasis is rendered rather than printed — see chat-reasoning.ts.

import { Fragment, useCallback, useId, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type { ToolCallMessagePartProps } from '@assistant-ui/react';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';
import { EmailActivityCard } from './ChatEmailCard.tsx';
import { useShell } from './ShellContext.tsx';
import { thoughtSegments } from '../surfaces/chat-reasoning.ts';
import type { ChatActivityItem } from '../surfaces/projections.ts';

/** The glyph per activity kind — operational vocabulary only (no tool/library names — G-197). */
const KIND_ICON: Readonly<Record<string, IconName>> = {
  memory_read: 'memory',
  memory_write: 'memory',
  govdoc_read: 'compass',
  file_read: 'files',
  file_write: 'files',
  helper_script: 'terminal',
  api_exploration: 'plug',
  task_proposed: 'tasks',
  kpi_proposed: 'kpis',
  schedule_drafted: 'clock',
  plan_edited: 'tasks', // the agent revising its own to-do list / plan (BUILD-DEC-098, owner clarification)
  thinking_summary: 'chat',
  email_send_requested: 'mail',
  email_sent: 'mail',
};

/** Read a string field off the opaque objectRef, or undefined (a malformed ref renders nothing extra). */
function refString(row: ChatActivityItem, key: string): string | undefined {
  const value = row.objectRef?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** The trace label the task-completion tool records on every outcome (a refused completion keeps it). */
const TASK_COMPLETION_LABEL = 'Completed the task';

/** Plain words for a step the gate held back. Still shown, never hidden: a refused completion means the
 *  task stays open, so it says exactly that; any other held step says it was not allowed yet. */
function heldLabel(rowLabel: string | undefined): string {
  return rowLabel === TASK_COMPLETION_LABEL ? 'not closed yet' : 'not allowed yet';
}

/** The honest outcome marker for a non-ok row (a refused/failed step is shown, never hidden). A gate hold
 *  is governance working as designed, so it reads in the warning tone rather than the failure tone. */
function OutcomeMark({
  outcome,
  rowLabel,
}: {
  readonly outcome: ChatActivityItem['outcome'];
  readonly rowLabel?: string;
}): React.JSX.Element | null {
  if (outcome === 'ok') return null;
  if (outcome === 'running') return <span className="tr-out tr-out-running">working…</span>;
  if (outcome === 'gate_refused') {
    return <span className="tr-out tr-out-held">{heldLabel(rowLabel)}</span>;
  }
  return <span className="tr-out">failed</span>;
}

/** The parent tree area for a memory node path (`operations/production/foo.md` → `operations/production`).
 *  Empty for a root-level node — the browser opens the root level and focuses the node either way. */
function areaOf(path: string | undefined): string {
  if (path === undefined) return '';
  const slash = path.lastIndexOf('/');
  return slash > 0 ? path.slice(0, slash) : '';
}

/** A memory_read row that resolves to a browsable node id opens the Memory browser focused on it
 *  (BUILD-DEC-098). A retrieved-node ref carries a `nodeId`; the aggregated promoted-layer row and any
 *  ref lacking a browsable id render NON-CLICKABLE (a broken/unlinkable target, shown honestly). */
function MemoryChip({ row }: { readonly row: ChatActivityItem }): React.JSX.Element {
  const shell = useShell();
  const nodeId = refString(row, 'nodeId');
  const title = refString(row, 'title');
  const inner = (
    <>
      <span className="tr-ico" aria-hidden="true">
        <Icon name={KIND_ICON[row.kind] ?? 'memory'} size={12} />
      </span>
      <span className="tr-label">{row.label}</span>
      {title !== undefined ? <span className="tr-ref">{title}</span> : null}
      <OutcomeMark outcome={row.outcome} rowLabel={row.label} />
    </>
  );
  if (nodeId === undefined) {
    // No browsable node id (the always-in-context aggregate, or a promoted entry with no source node) —
    // informative but not a link. Never a dead button (P-SELF): it renders as plain, non-interactive text.
    return (
      <div className="chat-tr-row" data-activity-kind={row.kind}>
        {inner}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="chat-tr-row chat-tr-click"
      data-activity-kind={row.kind}
      aria-label={`Open the memory note: ${title ?? row.label}`}
      onClick={() => shell.openMemoryNode(nodeId, areaOf(refString(row, 'path')))}
    >
      {inner}
    </button>
  );
}

/** One generic (non-clickable) trace row: icon + sanitised label + the ref's display title when present. */
function ActivityChip({ row }: { readonly row: ChatActivityItem }): React.JSX.Element {
  const title = refString(row, 'title') ?? refString(row, 'version');
  return (
    <div className="chat-tr-row" data-activity-kind={row.kind}>
      <span className="tr-ico" aria-hidden="true">
        <Icon name={KIND_ICON[row.kind] ?? 'info'} size={12} />
      </span>
      <span className="tr-label">{row.label}</span>
      {title !== undefined ? <span className="tr-ref">{title}</span> : null}
      <OutcomeMark outcome={row.outcome} rowLabel={row.label} />
    </div>
  );
}

/** One thinking summary's text with its markdown emphasis RENDERED rather than printed. The label reaches
 *  the screen exactly as recorded apart from its markers: the leading bold run is the step's own title, so
 *  it is set as one instead of arriving as a pair of literal asterisks (owner, 11 Aug 2026). */
export function ThoughtText({ label }: { readonly label: string }): React.JSX.Element {
  return (
    <>
      {thoughtSegments(label).map((segment, index) =>
        segment.strong ? (
          <strong className="tr-think-lead" key={`${String(index)}-${segment.text}`}>
            {segment.text}
          </strong>
        ) : (
          <Fragment key={`${String(index)}-${segment.text}`}>{segment.text}</Fragment>
        ),
      )}
    </>
  );
}

/**
 * One adjacent run of reasoning summaries, folded into ONE quiet disclosure (owner, 11 Aug 2026: the live
 * ticker is where a thought should pop; once the answer exists the summaries are "volume without value").
 * Collapsed by default, opened on a click, and the record is complete either way — every recorded summary in
 * the run is listed in order inside, nothing is dropped (P-SELF; this is presentation, not editing).
 *
 * It borrows the plan card's expand idiom (ChatPlan.tsx) rather than inventing one: the same icon + mono
 * microlabel + count chip + rotating chevron, so the dock has one expand gesture and not two. The header
 * keeps identical dimensions in both states, so toggling moves the answer and never redraws the row itself.
 */
export function ReasoningSummaryDisclosure({
  rows,
}: {
  readonly rows: readonly ChatActivityItem[];
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const toggle = useCallback(() => setOpen((current) => !current), []);
  // A run that thought nothing shows nothing — an empty disclosure would claim work that never happened.
  if (rows.length === 0) return null;
  return (
    <div className="chat-tr-think-group" data-activity-kind="thinking_summary">
      <button
        type="button"
        className="trg-head"
        aria-expanded={open}
        {...(open ? { 'aria-controls': panelId } : {})}
        onClick={toggle}
      >
        <span className="trg-ico" aria-hidden="true">
          <Icon name="chat" size={12} />
        </span>
        <span className="trg-title">Thinking</span>
        <span className="trg-count">
          {rows.length === 1 ? '1 step' : `${String(rows.length)} steps`}
        </span>
        <span className={open ? 'trg-chev open' : 'trg-chev'} aria-hidden="true">
          <Icon name="chevdown" size={12} />
        </span>
      </button>
      {open ? (
        <ol id={panelId} className="trg-steps">
          {rows.map((row, index) => (
            // The module mints unique ids, but the key stays collision-proof if a projection ever repeated
            // one — a duplicate React key silently drops a step, which here would hide a recorded thought.
            <li className="trg-step" key={`${row.id}-${String(index)}`} data-reasoning-step>
              <span className="tr-think ritona-voice">
                <ThoughtText label={row.label} />
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

/** One trace row, dispatched by kind. */
export function ActivityItem({
  row,
  refusedBefore = 0,
}: {
  readonly row: ChatActivityItem;
  /** Attempts that preceded this one and were collapsed into it — see `collapseEmailAttempts`. */
  readonly refusedBefore?: number;
}): React.JSX.Element {
  if (row.kind === 'email_sent' || row.kind === 'email_send_requested') {
    return <EmailActivityCard row={row} refusedBefore={refusedBefore} />;
  }
  // A lone summary that reaches the dispatcher un-folded still takes the disclosure, so the chat has exactly
  // one way of showing a recorded thought — a one-step card, quiet by default like the rest.
  if (row.kind === 'thinking_summary') return <ReasoningSummaryDisclosure rows={[row]} />;
  if (row.kind === 'memory_read' || row.kind === 'memory_write') return <MemoryChip row={row} />;
  return <ActivityChip row={row} />;
}

/** Narrow the view runtime's opaque UI artifact back to the sanitised row this client placed there.
 *  A malformed artifact renders nothing; a raw tool name/argument/result is never used as fallback copy. */
function isActivityRow(value: unknown): value is ChatActivityItem {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row['id'] === 'string' &&
    typeof row['kind'] === 'string' &&
    typeof row['label'] === 'string' &&
    (row['sequence'] === undefined || typeof row['sequence'] === 'number') &&
    (row['outcome'] === 'running' ||
      row['outcome'] === 'ok' ||
      row['outcome'] === 'error' ||
      row['outcome'] === 'gate_refused')
  );
}

/** Narrow the folded reasoning group the general chat's bridge places on its synthetic part. The general
 *  chat flattens each row into its own part before anything renders, so the fold has to travel as an
 *  artifact — see `convertTurn`. A malformed artifact renders nothing, same rule as a single row. */
function isSummaryGroup(value: unknown): value is { readonly rows: readonly ChatActivityItem[] } {
  if (typeof value !== 'object' || value === null) return false;
  const group = value as Record<string, unknown>;
  return (
    group['kind'] === 'thinking_summary_group' &&
    Array.isArray(group['rows']) &&
    group['rows'].every((row) => isActivityRow(row))
  );
}

/** Render-only tool part adapter. The call already ran server-side; this component intentionally never
 *  invokes `addResult`, `resume`, or any client tool execution path. */
export function ActivityToolPart({ artifact }: ToolCallMessagePartProps): React.JSX.Element | null {
  if (isSummaryGroup(artifact)) return <ReasoningSummaryDisclosure rows={artifact.rows} />;
  if (!isActivityRow(artifact)) return null;
  return <ActivityItem row={artifact} />;
}

/** Preserve the established compact trace treatment around the view runtime's consecutive structured
 *  activity/reasoning parts. */
export function ActivityPartGroup({ children }: PropsWithChildren): React.JSX.Element {
  return (
    <div className="chat-trace" data-testid="chat-activity-trace" aria-label="What Ritona did">
      {children}
    </div>
  );
}

/** The trace group rendered above an agent answer (or in the live thinking area): every durable row, in
 *  the recorded order — nothing sampled, nothing fabricated. */
export function ActivityTrace({
  rows,
}: {
  readonly rows: readonly ChatActivityItem[];
}): React.JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <div className="chat-trace" data-testid="chat-activity-trace" aria-label="What Ritona did">
      {rows.map((row) => (
        <ActivityItem key={row.id} row={row} />
      ))}
    </div>
  );
}
